import { AnalyticsService, peekLoginMethod, takeLoginMethod } from '@alison/analytics/client';
import { Button } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { TRPCClientError } from '@trpc/client';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { TenantLogo } from '@/components/TenantLogo.js';
import { signOut, useSession } from '../../../lib/authClient.js';
import { mintJwtCookie, setSelectionCookie } from '../../../lib/cookies.js';
import { useTRPC } from '../../../lib/trpc.js';

function reachedServer(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data !== null && error.data !== undefined;
}

function isAccountNotFound(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false;
  const data = error.data as { code?: string } | undefined;
  return data?.code === 'NOT_FOUND';
}

function CenteredScreen({ children }: { children: ReactNode }): ReactNode {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}

function SignInPage(): ReactNode {
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const { providerName } = useTenant();
  const trpc = useTRPC();
  const [selectedId, setSelectedId] = useState('');
  const [minted, setMinted] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (session !== null && !minted) {
      void mintJwtCookie().then((ok) => {
        if (!ok) console.error('[auth] sign-in: failed to mint the jwt cookie');
        setMinted(true);
      });
    }
  }, [session, minted]);

  const clientsQuery = useQuery({
    ...trpc.auth.getClients.queryOptions(),
    enabled: session !== null && minted,
  });
  const loginMutation = useMutation(
    trpc.auth.login.mutationOptions({ trpc: { context: { stream: false } } }),
  );
  const clients = clientsQuery.data;

  useEffect(() => {
    if (clientsQuery.error) {
      console.error('[auth] sign-in: getClients failed', clientsQuery.error);
    }
  }, [clientsQuery.error]);

  const runLogin = useCallback(
    async (client: NonNullable<typeof clients>[number]): Promise<void> => {
      setLoginError(null);
      // me.get (and with it AnalyticsService.identify()) is disabled on /login routes,
      // so exclusion is not known yet here — check the session email directly.
      const excluded =
        session?.user?.email !== undefined && AnalyticsService.isExcludedEmail(session.user.email);
      const method = peekLoginMethod();
      try {
        await loginMutation.mutateAsync({
          clientId: client.id,
          ...(method !== undefined && { method }),
        });
        takeLoginMethod();
        if (!excluded) {
          AnalyticsService.register({
            clientId: client.id,
            brandName: client.name,
            accountIds: client.accounts.map((account) => account.id),
            ...(session?.user?.email !== undefined && { userEmail: session.user.email }),
            ...(session?.user?.name !== undefined &&
              session.user.name !== '' && { userName: session.user.name }),
          });
        }
        setSelectionCookie(client.accounts.map((account) => account.id));
        void navigate({ to: '/dashboard' });
      } catch (error) {
        console.error('[auth] sign-in: login mutation failed', { clientId: client.id, error });
        if (!excluded && !reachedServer(error)) {
          AnalyticsService.track('Auth - Login Failed', {
            stage: 'brand-login',
            reason: 'transport-failure',
            ...(method !== undefined && { method }),
            clientId: client.id,
            brandName: client.name,
          });
        }
        setLoginError('Sign-in failed. Please try again.');
      }
    },
    [loginMutation, navigate, session],
  );

  useEffect(() => {
    if (!isPending && session === null) {
      void navigate({ to: '/login' });
    }
  }, [isPending, session, navigate]);

  useEffect(() => {
    const only = clients?.length === 1 ? clients[0] : undefined;
    if (only && loginMutation.isIdle) {
      void runLogin(only);
    }
  }, [clients, loginMutation.isIdle, runLogin]);

  async function switchAccount(): Promise<void> {
    await signOut();
    await navigate({ to: '/login' });
  }

  if (clientsQuery.error) {
    const notFound = isAccountNotFound(clientsQuery.error);
    return (
      <CenteredScreen>
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <TenantLogo />
          </Box>
          <Alert
            severity={notFound ? 'info' : 'error'}
            data-testid={notFound ? 'account-not-found' : 'login-error'}
            sx={{ width: '100%' }}
            action={
              <Button
                color="inherit"
                size="small"
                data-testid="use-different-account"
                onClick={() => void switchAccount()}
              >
                {notFound ? 'Use a different account' : 'Sign in again'}
              </Button>
            }
          >
            {notFound
              ? `${providerName} has no account for ${session?.user?.email ?? 'this email'}. Ask your team admin to invite you, or sign in with a different work account.`
              : 'Sign-in failed. Please try again.'}
          </Alert>
        </Box>
      </CenteredScreen>
    );
  }

  const multipleClients = clients !== undefined && clients.length > 1;
  if (
    isPending ||
    session === null ||
    clientsQuery.isPending ||
    (!multipleClients && loginError === null)
  ) {
    return (
      <CenteredScreen>
        <CircularProgress />
      </CenteredScreen>
    );
  }

  function onContinue(): void {
    const client = clients?.find((entry) => String(entry.id) === selectedId);
    if (client) void runLogin(client);
  }

  return (
    <CenteredScreen>
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <TenantLogo />
        </Box>

        <Typography
          variant="h1"
          align="center"
          sx={(theme) => ({ ...theme.mixins.gradientText, mb: 1 })}
        >
          Choose your brand
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Select the brand you want to work on to continue.
        </Typography>

        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: 5,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            boxShadow: 5,
          }}
        >
          <Stack spacing={5}>
            <FormControl fullWidth>
              <Typography
                id="sign-in-brand-label"
                variant="caption"
                sx={{ color: 'text.primary', mb: 2, display: 'block' }}
              >
                Brand
              </Typography>
              <Select
                value={selectedId}
                onChange={(event) => setSelectedId(event.target.value)}
                displayEmpty
                aria-labelledby="sign-in-brand-label"
                data-testid="select-client"
                MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
                renderValue={(value) =>
                  value ? (
                    clients?.find((client) => String(client.id) === value)?.name
                  ) : (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      Select a brand
                    </Box>
                  )
                }
                sx={{
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': { py: 1.5 },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                {clients?.map((client) => (
                  <MenuItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loginError && (
              <Alert
                severity="error"
                data-testid="login-error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    data-testid="login-error-sso"
                    onClick={() => void navigate({ to: '/login' })}
                  >
                    Sign in again
                  </Button>
                }
              >
                {loginError}
              </Alert>
            )}

            <Button
              fullWidth
              disabled={selectedId === '' || loginMutation.isPending}
              onClick={() => onContinue()}
              data-testid="select-client-continue"
              sx={{
                py: 1.25,
                borderRadius: 1,
                color: 'common.white',
                fontWeight: 600,
                background: (theme) => theme.gradients.primary,
                boxShadow: 4,
                '&:hover': { background: (theme) => theme.gradients.primaryHover },
                '&.Mui-disabled': { color: 'common.white', opacity: 0.45 },
              }}
            >
              Continue
            </Button>
          </Stack>
        </Paper>
      </Box>
    </CenteredScreen>
  );
}

export const Route = createFileRoute('/login/sign-in/')({
  component: SignInPage,
});
